import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../lib/cn";

export type PopoverPlacement = "bottom-start" | "bottom-end" | "top-start" | "top-end";

export interface PopoverProps {
  /** Élément sous lequel (ou au-dessus duquel) le panneau se pose. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Appelé sur Échap, clic extérieur, ou disparition de l'ancre. */
  onClose: () => void;
  /** Position préférée. Le panneau bascule tout seul s'il manque la place. */
  placement?: PopoverPlacement;
  /** Le panneau adopte la largeur exacte de l'ancre (listes de sélection). */
  matchAnchorWidth?: boolean;
  /** Largeur fixe en pixels quand le panneau ne suit pas l'ancre. */
  width?: number;
  /** Écart entre l'ancre et le panneau, en pixels. */
  gap?: number;
  className?: string;
  children: ReactNode;
}

/** Marge minimale conservée entre le panneau et le bord de la fenêtre. */
const VIEWPORT_MARGIN = 8;

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Panneau flottant ancré à un élément.
 *
 * ── Pourquoi ce composant existe ─────────────────────────────────────────────
 * Un popover écrit « à la main » est presque toujours un `absolute z-50` posé
 * dans un conteneur `relative`. C'est faux, et ça se voit dès que l'interface
 * grandit :
 *
 *   1. `z-index` n'est pas un classement global. Il ne trie que des frères DANS
 *      un même contexte d'empilement. Une carte qui porte `transform`,
 *      `filter`, `opacity < 1`, `contain` ou son propre `z-index` en crée un :
 *      le `z-50` de la liste déroulante n'y vaut alors plus que « au-dessus de
 *      mes frères à moi », et la carte suivante lui passe devant. C'est le
 *      symptôme classique du calendrier qui glisse SOUS la carte d'à côté.
 *
 *   2. Un ancêtre en `overflow: hidden` ou `overflow: auto` — une carte à coins
 *      arrondis, une zone de défilement — découpe le panneau. Aucun `z-index`
 *      ne répare un découpage : ce n'est pas un problème d'ordre, mais de boîte.
 *
 * D'où les deux décisions structurantes :
 *
 *   • Le panneau est PORTALISÉ dans `<body>`. Il n'a donc plus aucun ancêtre
 *     susceptible de le découper ou de l'enfermer dans un contexte d'empilement,
 *     et sa position devient un simple calcul depuis le rectangle de l'ancre.
 *
 *   • Il est promu dans la TOP LAYER via l'API `popover` native. La top layer
 *     est la couche que le navigateur peint au-dessus de tout le document,
 *     modales natives comprises — ce n'est plus une surenchère de `z-index`,
 *     c'est une couche à part. On garde `z-index` en repli pour les navigateurs
 *     qui n'ont pas l'API.
 *
 * Le mode `manual` est choisi volontairement plutôt que `auto` : `auto` ferme
 * le panneau au moindre clic extérieur, y compris sur l'ancre — le bouton
 * refermerait puis rouvrirait aussitôt, et une bascule ne marcherait jamais.
 * On gère donc la fermeture nous-mêmes, en excluant l'ancre.
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  placement = "bottom-start",
  matchAnchorWidth = false,
  width,
  gap = 6,
  className,
  children,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<{ top: number; left: number; width?: number }>({
    top: 0,
    left: 0,
  });
  const [flipped, setFlipped] = useState(false);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const panelWidth = matchAnchorWidth ? rect.width : (width ?? panelRect.width);
    const panelHeight = panelRect.height;

    const wantsTop = placement.startsWith("top");
    const spaceBelow = window.innerHeight - rect.bottom - gap - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - gap - VIEWPORT_MARGIN;

    // On ne bascule que si la place manque VRAIMENT du côté demandé et qu'il y
    // en a davantage de l'autre : un panneau qui saute d'un côté à l'autre au
    // moindre pixel est plus déroutant qu'un panneau un peu à l'étroit.
    const shouldFlip = wantsTop
      ? spaceAbove < panelHeight && spaceBelow > spaceAbove
      : spaceBelow < panelHeight && spaceAbove > spaceBelow;
    const onTop = wantsTop ? !shouldFlip : shouldFlip;

    let top = onTop ? rect.top - panelHeight - gap : rect.bottom + gap;
    top = Math.min(
      Math.max(top, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - panelHeight - VIEWPORT_MARGIN),
    );

    let left = placement.endsWith("end") ? rect.right - panelWidth : rect.left;
    left = Math.min(
      Math.max(left, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - panelWidth - VIEWPORT_MARGIN),
    );

    setFlipped(onTop);
    setStyle({ top, left, width: matchAnchorWidth ? rect.width : width });
  }, [anchorRef, gap, matchAnchorWidth, placement, width]);

  // Promotion dans la top layer, puis premier calcul de position AVANT peinture :
  // un panneau qui apparaît à (0,0) le temps d'une image est un saut visible.
  useIsoLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    if (typeof panel.showPopover === "function") {
      try {
        panel.showPopover();
      } catch {
        // Déjà dans la top layer (double montage du mode strict) : sans intérêt.
      }
    }
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;

    // `capture: true` : le défilement d'un conteneur interne ne remonte pas en
    // phase de bulle jusqu'à `window`. Sans capture, un panneau ouvert dans une
    // zone défilante resterait figé pendant que son ancre s'en va.
    const onScrollOrResize = () => reposition();
    window.addEventListener("scroll", onScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    // Le contenu peut changer de taille sans que rien ne défile (filtrage d'une
    // liste, passage d'un mois à l'autre) : le repli haut/bas doit suivre.
    const observer = new ResizeObserver(onScrollOrResize);
    if (panelRef.current) observer.observe(panelRef.current);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    // `pointerdown` et non `click` : la fermeture doit précéder le relâchement,
    // sinon un clic sur un bouton extérieur déclenche son action ET laisse le
    // panneau ouvert le temps d'un rendu.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize, { capture: true });
      window.removeEventListener("resize", onScrollOrResize);
      observer.disconnect();
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, onClose, reposition, anchorRef]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      popover="manual"
      data-nexty-popover=""
      data-placement={flipped ? "top" : "bottom"}
      style={{
        position: "fixed",
        top: style.top,
        left: style.left,
        width: style.width,
        // La feuille de style du navigateur donne aux `[popover]` une marge
        // automatique, un padding de 0.25em et un fond `canvas`. On neutralise
        // les deux premiers ici ; le fond est repris par les classes.
        margin: 0,
        padding: 0,
        // Repli pour les navigateurs sans API `popover` : on retombe sur un
        // empilement classique, qui suffit dès lors qu'on est enfant de <body>.
        zIndex: 60,
      }}
      className={cn(
        "max-h-[min(32rem,calc(100dvh-2rem))] overflow-y-auto overscroll-contain rounded-xl border border-border",
        "bg-surface-raised text-fg shadow-overlay box-border scrollbar-thin",
        "animate-scale-in",
        flipped ? "origin-bottom" : "origin-top",
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
